<script setup lang="ts">
	import femaleAvatar from '@/assets/imgs/femaie_avatar.png'
	import maleAvatar from '@/assets/imgs/male_avantar.png'
	import BreadCrumb from '@/components/BreadCrumb.vue'
	import LoadingModal from '@/components/LoadingModal.vue'
	import usePendingRequest from '@/modules/pendingRequests/composables/usePendingRequest'
	import { NewDataUser } from '@/modules/pendingRequests/interfaces/pendingRequest'
	import FormApprovalUser from '@/modules/users/components/FormApprovalUser.vue'
	import { computed, ref, watch } from 'vue'
	import { useRoute } from 'vue-router'

	const route = useRoute()

	const { approvalRequests, approvalIsLoading } = usePendingRequest<NewDataUser>({
		immediate: true,
		params: { id: route.params.id as string },
	})

	const pages = ref<{ name: string; to: object; current: boolean }[]>([])

	watch(
		approvalRequests,
		() => {
			pages.value = [
				{ name: 'Usuarios', to: { name: 'users.index' }, current: false },
				{ name: 'Solicitudes', to: { name: 'users.approvals' }, current: false },
				{
					name: approvalRequests.value
						? `${approvalRequests.value.newData.name} ${approvalRequests.value.newData.lastname}`
						: 'Detalle',
					to: { name: 'approval.edit', params: { id: route.params.id } },
					current: true,
				},
			]
		},
		{ immediate: true }
	)

	const avatarSrc = computed(() => {
		const gender = approvalRequests.value?.newData.gender
		return gender === 'femenino' ? femaleAvatar : maleAvatar
	})

	const stateBadge = computed(() => {
		const state = approvalRequests.value?.state
		if (state === 'approved') return { text: 'Aprobada', cls: 'bg-green-50 text-green-700 border-green-200' }
		if (state === 'rejected') return { text: 'Rechazada', cls: 'bg-red-50 text-red-700 border-red-200' }
		return { text: 'Pendiente', cls: 'bg-amber-50 text-amber-700 border-amber-200' }
	})
</script>

<template>
	<div class="min-h-screen bg-slate-50/50">
		<BreadCrumb :pages="pages" />

		<main class="container mx-auto mt-4 px-4 pb-12 max-w-3xl">
			<div class="flex flex-col gap-6">

				<!-- User info card -->
				<div class="bg-white rounded-xl border shadow-sm overflow-hidden">
					<!-- Card header -->
					<div class="flex items-center justify-between px-6 py-4 border-b border-gray-100">
						<div class="flex items-center gap-3">
							<div class="w-9 h-9 rounded-xl bg-[#021F58] flex items-center justify-center flex-shrink-0">
								<svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
								</svg>
							</div>
							<div>
								<h2 class="text-sm font-semibold text-gray-900 leading-tight">Información básica</h2>
								<p class="text-xs text-gray-400 leading-tight">Datos del solicitante</p>
							</div>
						</div>
						<span
							class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border"
							:class="stateBadge.cls"
						>
							{{ stateBadge.text }}
						</span>
					</div>

					<!-- User details -->
					<div class="p-6">
						<div class="flex flex-col sm:flex-row items-center sm:items-start gap-6">
							<!-- Avatar -->
							<div class="flex-shrink-0">
								<img
									:src="avatarSrc"
									alt="Avatar"
									class="w-28 h-28 object-cover rounded-2xl border-2 border-gray-100"
								/>
							</div>

							<!-- Fields grid -->
							<div class="grid grid-cols-2 gap-x-8 gap-y-4 flex-1">
								<div>
									<p class="text-xs font-medium text-gray-400 uppercase tracking-wide mb-0.5">Nombre</p>
									<p class="text-sm font-semibold text-gray-900">
										{{ approvalRequests?.newData.name ?? '—' }}
									</p>
								</div>
								<div>
									<p class="text-xs font-medium text-gray-400 uppercase tracking-wide mb-0.5">Apellido</p>
									<p class="text-sm font-semibold text-gray-900">
										{{ approvalRequests?.newData.lastname ?? '—' }}
									</p>
								</div>
								<div>
									<p class="text-xs font-medium text-gray-400 uppercase tracking-wide mb-0.5">Segundo nombre</p>
									<p class="text-sm text-gray-600">
										{{ approvalRequests?.newData.secondname ?? 'No tiene' }}
									</p>
								</div>
								<div>
									<p class="text-xs font-medium text-gray-400 uppercase tracking-wide mb-0.5">Segundo apellido</p>
									<p class="text-sm text-gray-600">
										{{ approvalRequests?.newData.secondlastname ?? 'No tiene' }}
									</p>
								</div>
								<div class="col-span-2">
									<p class="text-xs font-medium text-gray-400 uppercase tracking-wide mb-0.5">Correo</p>
									<p class="text-sm text-gray-600">
										{{ approvalRequests?.newData.email || '—' }}
									</p>
								</div>
							</div>
						</div>
					</div>
				</div>

				<!-- Assignment form card -->
				<div
					v-if="approvalRequests"
					class="bg-white rounded-xl border shadow-sm overflow-hidden"
				>
					<div class="flex items-center gap-3 px-6 py-4 border-b border-gray-100">
						<div class="w-9 h-9 rounded-xl bg-[#021F58] flex items-center justify-center flex-shrink-0">
							<svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
							</svg>
						</div>
						<div>
							<h2 class="text-sm font-semibold text-gray-900 leading-tight">Asignación</h2>
							<p class="text-xs text-gray-400 leading-tight">Completa los datos para aprobar</p>
						</div>
					</div>

					<div class="p-6">
						<FormApprovalUser
							:companyId="approvalRequests.newData.companyId"
							:approvalId="approvalRequests.id"
							:approvalState="approvalRequests.state"
						/>
					</div>
				</div>

			</div>
		</main>

		<LoadingModal :when="approvalIsLoading" />
	</div>
</template>
